import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { Dialog } from '@mui/material'
import Checkbox from 'components/atoms/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined'
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined'
import FestivalOutlinedIcon from '@mui/icons-material/FestivalOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

const UnitsSyncModal = ({
  open,
  onConfirm,
  onSkip,
  previewData,
  osSelectedUprns = [],
  nearbyExpandedFallback = false,
  loading,
  confirming
}) => {
  const [selectedUprns, setSelectedUprns] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  const matched = useMemo(() => previewData?.matched || [], [previewData])
  const unmatched = useMemo(() => previewData?.unmatched || [], [previewData])
  const referenceAddress = previewData?.reference_address || null
  const referenceUprn = previewData?.reference_uprn
    ? String(previewData.reference_uprn)
    : null
  const nearbyExpanded =
    Boolean(previewData?.nearby_expanded) || Boolean(nearbyExpandedFallback)
  const checkedPostcodes = previewData?.checked_postcodes || []
  const nearbyAttemptedRadii = previewData?.nearby_attempted_radii || []
  const nearbyFoundRadius = previewData?.nearby_found_radius || null
  const nearbySearchResult =
    previewData?.nearby_search_result || 'not_attempted'
  const nearbyMaxRadiusReached = Boolean(
    previewData?.nearby_search_max_radius_reached
  )
  const nearbyCurrentRadius = previewData?.nearby_current_radius || null
  const referencePostcode = previewData?.reference_postcode || null
  const referencePostcodeRawCount = Number(
    previewData?.reference_postcode_raw_count || 0
  )
  const referencePostcodeDedupedCount = Number(
    previewData?.reference_postcode_deduped_count || 0
  )
  const referencePostcodeMatchedCount = Number(
    previewData?.reference_postcode_matched_count || 0
  )
  const matchedFromReferencePostcodeCount = Number(
    previewData?.matched_from_reference_postcode_count || 0
  )
  const matchedFromNearbyPostcodesCount = Number(
    previewData?.matched_from_nearby_postcodes_count || 0
  )
  const unmatchedFromNearbyPostcodesCount = Number(
    previewData?.unmatched_from_nearby_postcodes_count || 0
  )
  const nearbyDiscoveredPostcodesCount = Number(
    previewData?.nearby_discovered_postcodes_count || 0
  )
  const nearbyDiscoveredPostcodesTotalCount = Number(
    previewData?.nearby_discovered_postcodes_total_count || 0
  )
  const nearbyPostcodeLimitReached = Boolean(
    previewData?.nearby_postcode_limit_reached
  )
  const nearbyPostcodesPagesChecked = Number(
    previewData?.nearby_postcodes_pages_checked || 0
  )
  const nearbyPostcodesPagesAvailable = Array.isArray(
    previewData?.nearby_postcodes_pages_available
  )
    ? previewData.nearby_postcodes_pages_available
    : []
  const nearbyPostcodesPageLimitReached = Boolean(
    previewData?.nearby_postcodes_page_limit_reached
  )
  const nearbyReversegeoPageLimitReached = Boolean(
    previewData?.nearby_reversegeo_page_limit_reached
  )
  const nearbyRawCount = Number(previewData?.nearby_raw_count || 0)
  const nearbyDedupedCount = Number(previewData?.nearby_deduped_count || 0)
  const previewResultSummary = previewData?.preview_result_summary || null
  const nearbyReviewCount =
    matchedFromNearbyPostcodesCount + unmatchedFromNearbyPostcodesCount
  const totalCandidateCount = matched.length + unmatched.length
  const resultsMayBeTruncated = Boolean(
    previewData?.reference_postcode_page_limit_reached ||
      nearbyPostcodesPageLimitReached ||
      nearbyReversegeoPageLimitReached ||
      nearbyPostcodeLimitReached
  )

  const [movedToRight, setMovedToRight] = useState(new Set())
  const [movedToLeft, setMovedToLeft] = useState(new Set())
  const osSelectedUprnSet = useMemo(
    () =>
      new Set(
        (Array.isArray(osSelectedUprns) ? osSelectedUprns : [])
          .map(uprn => String(uprn || '').trim())
          .filter(Boolean)
      ),
    [osSelectedUprns]
  )

  const isReferenceItem = item =>
    Boolean(referenceUprn) && String(item?.uprn || '') === referenceUprn
  const shouldShowUnmatchedInReview = () => true

  const leftItems = useMemo(
    () => [
      ...unmatched.filter(
        item =>
          !movedToRight.has(item.uprn) &&
          !isReferenceItem(item) &&
          shouldShowUnmatchedInReview(item)
      ),
      ...matched.filter(
        item => movedToLeft.has(item.uprn) && !isReferenceItem(item)
      )
    ],
    [unmatched, matched, movedToRight, movedToLeft, referenceUprn]
  )

  const rightItems = useMemo(
    () => [
      ...matched.filter(
        item =>
          !movedToLeft.has(item.uprn) &&
          !item.already_exists &&
          !isReferenceItem(item)
      ),
      ...unmatched.filter(
        item => movedToRight.has(item.uprn) && !isReferenceItem(item)
      )
    ],
    [matched, unmatched, movedToLeft, movedToRight, referenceUprn]
  )

  const missingOsSelectedUprns = useMemo(() => {
    if (!osSelectedUprnSet.size) {
      return []
    }

    const previewUprns = new Set(
      [...matched, ...unmatched]
        .map(item => String(item?.uprn || '').trim())
        .filter(Boolean)
    )

    return [...osSelectedUprnSet].filter(uprn => !previewUprns.has(uprn))
  }, [matched, unmatched, osSelectedUprnSet])

  const existingItems = useMemo(
    () => matched.filter(item => item.already_exists && !isReferenceItem(item)),
    [matched, referenceUprn]
  )
  const hasRawPreviewResults = matched.length > 0 || unmatched.length > 0
  const hasActionablePreviewResults =
    leftItems.length > 0 || rightItems.length > 0
  const normalizedSearchQuery = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery]
  )

  const filteredLeftItems = useMemo(() => {
    if (!normalizedSearchQuery) {
      return leftItems
    }

    return leftItems.filter(item => {
      const searchableText = [
        item.uprn,
        item.postcode,
        item.address,
        item.unit_name,
        item.building_name,
        item.thoroughfare_name,
        item.building_type_label,
        item.parent_uprn,
        item.root_uprn,
        item.hierarchy_level,
        item.topography_layer_toid
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(normalizedSearchQuery)
    })
  }, [leftItems, normalizedSearchQuery])

  const filteredRightItems = useMemo(() => {
    if (!normalizedSearchQuery) {
      return rightItems
    }

    return rightItems.filter(item => {
      const searchableText = [
        item.uprn,
        item.postcode,
        item.address,
        item.unit_name,
        item.building_name,
        item.thoroughfare_name,
        item.building_type_label,
        item.parent_uprn,
        item.root_uprn,
        item.hierarchy_level,
        item.topography_layer_toid
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(normalizedSearchQuery)
    })
  }, [rightItems, normalizedSearchQuery])

  const buildGroups = items => {
    const groups = {}
    items.forEach(item => {
      const normalizedBuildingName = String(item.building_name || '')
        .trim()
        .toLowerCase()
      const key =
        item.topography_layer_toid ||
        item.parent_uprn ||
        (normalizedBuildingName
          ? `building:${normalizedBuildingName}`
          : null) ||
        item.uprn ||
        'unknown'
      if (!groups[key]) {
        groups[key] = {
          key,
          toid: item.topography_layer_toid || null,
          parentUprn: item.parent_uprn || null,
          items: [],
          buildingName: null,
          thoroughfareName: null
        }
      }
      groups[key].items.push(item)
      if (!groups[key].buildingName && item.building_name) {
        groups[key].buildingName = item.building_name
      }
      if (!groups[key].thoroughfareName && item.thoroughfare_name) {
        groups[key].thoroughfareName = item.thoroughfare_name
      }
    })
    return Object.values(groups)
  }

  const buildGroupsByStreet = items => {
    const groups = {}
    items.forEach(item => {
      const key =
        item.postcode ||
        item.thoroughfare_name ||
        item.topography_layer_toid ||
        item.parent_uprn ||
        'Other addresses'
      if (!groups[key]) {
        groups[key] = {
          key,
          toid: null,
          parentUprn: null,
          items: [],
          buildingName: null,
          postcode: item.postcode || null,
          thoroughfareName: item.thoroughfare_name || null,
          groupingMode: 'review'
        }
      }
      groups[key].items.push(item)
      if (!groups[key].postcode && item.postcode) {
        groups[key].postcode = item.postcode
      }
      if (!groups[key].thoroughfareName && item.thoroughfare_name) {
        groups[key].thoroughfareName = item.thoroughfare_name
      }
    })
    return Object.values(groups)
  }

  const leftGroups = useMemo(
    () => buildGroupsByStreet(filteredLeftItems),
    [filteredLeftItems]
  )
  const rightGroups = useMemo(
    () => buildGroups(filteredRightItems),
    [filteredRightItems]
  )

  const [collapsedGroups, setCollapsedGroups] = useState(new Set())

  useEffect(() => {
    if (!leftGroups.length) return
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      leftGroups.forEach(group => {
        if (group.items.length > 1) {
          next.add(`left-${group.key}`)
        }
      })
      return next
    })
  }, [leftGroups.length])

  useEffect(() => {
    if (!osSelectedUprnSet.size || !previewData) {
      return
    }

    setMovedToRight(prev => {
      const next = new Set(prev)
      unmatched.forEach(item => {
        if (osSelectedUprnSet.has(String(item?.uprn || ''))) {
          next.add(item.uprn)
        }
      })
      return next
    })

    setMovedToLeft(prev => {
      const next = new Set(prev)
      matched.forEach(item => {
        if (osSelectedUprnSet.has(String(item?.uprn || ''))) {
          next.delete(item.uprn)
        } else {
          next.add(item.uprn)
        }
      })
      return next
    })
  }, [matched, unmatched, osSelectedUprnSet, previewData])
  const toggleGroup = key => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const toggleSelect = uprn => {
    setSelectedUprns(prev => {
      const next = new Set(prev)
      next.has(uprn) ? next.delete(uprn) : next.add(uprn)
      return next
    })
  }

  const updateSelectionForItems = (items, shouldSelect) => {
    setSelectedUprns(prev => {
      const next = new Set(prev)
      items.forEach(item => {
        if (!item?.uprn) return
        if (shouldSelect) {
          next.add(item.uprn)
        } else {
          next.delete(item.uprn)
        }
      })
      return next
    })
  }

  const moveSelectedToRight = () => {
    const leftUprns = new Set(leftItems.map(i => i.uprn))
    selectedUprns.forEach(uprn => {
      if (!leftUprns.has(uprn)) return
      if (unmatched.find(i => i.uprn === uprn)) {
        setMovedToRight(prev => new Set([...prev, uprn]))
      }
      if (matched.find(i => i.uprn === uprn)) {
        setMovedToLeft(prev => {
          const next = new Set(prev)
          next.delete(uprn)
          return next
        })
      }
    })
    setSelectedUprns(new Set())
  }

  const moveSelectedToLeft = () => {
    const rightUprns = new Set(rightItems.map(i => i.uprn))
    selectedUprns.forEach(uprn => {
      if (!rightUprns.has(uprn)) return
      if (matched.find(i => i.uprn === uprn)) {
        setMovedToLeft(prev => new Set([...prev, uprn]))
      }
      if (unmatched.find(i => i.uprn === uprn)) {
        setMovedToRight(prev => {
          const next = new Set(prev)
          next.delete(uprn)
          return next
        })
      }
    })
    setSelectedUprns(new Set())
  }

  const handleConfirm = () => {
    const uprns = rightItems.map(item => item.uprn)
    onConfirm({
      uprns,
      previewContext: {
        nearbyExpanded,
        nearbyRadiusIndex: Number.isInteger(previewData?.nearby_radius_index)
          ? previewData.nearby_radius_index
          : null
      }
    })
  }

  const leftVisibleSelectedCount = useMemo(
    () => filteredLeftItems.filter(item => selectedUprns.has(item.uprn)).length,
    [filteredLeftItems, selectedUprns]
  )
  const rightVisibleSelectedCount = useMemo(
    () =>
      filteredRightItems.filter(item => selectedUprns.has(item.uprn)).length,
    [filteredRightItems, selectedUprns]
  )
  const allVisibleLeftSelected =
    filteredLeftItems.length > 0 &&
    leftVisibleSelectedCount === filteredLeftItems.length
  const allVisibleRightSelected =
    filteredRightItems.length > 0 &&
    rightVisibleSelectedCount === filteredRightItems.length
  const totalVisibleResults =
    filteredLeftItems.length + filteredRightItems.length

  useBodyScrollLock(open)

  const totalToCreate = rightItems.length
  const nearbyRadiusText = nearbyAttemptedRadii.length
    ? nearbyAttemptedRadii.map(radius => `${radius}m`).join(' and ')
    : null
  const nearbyRadiusProgressText = nearbyAttemptedRadii.length
    ? nearbyAttemptedRadii.map(radius => `${radius}m`).join(' -> ')
    : null
  const postcodeScopeText = checkedPostcodes.length
    ? `${checkedPostcodes.length} postcode${checkedPostcodes.length !== 1 ? 's' : ''}`
    : 'the selected building postcode'
  const nearbyPagesStatusText = nearbyExpanded
    ? nearbyPostcodesPagesChecked > 0
      ? 'Nearby addresses were included in this review.'
      : nearbyDiscoveredPostcodesCount > 0
        ? 'Nearby addresses were checked, but no extra unit suggestions were needed.'
        : 'No extra nearby addresses were found.'
    : null
  const truncationWarningText = resultsMayBeTruncated
    ? `This result may still be incomplete because the search hit one of the configured caps${nearbyPostcodeLimitReached ? ` (showing ${nearbyDiscoveredPostcodesCount} of ${nearbyDiscoveredPostcodesTotalCount} nearby postcode${nearbyDiscoveredPostcodesTotalCount !== 1 ? 's' : ''})` : ''}.`
    : null
  const summaryHeadline = nearbyExpanded
    ? `Found ${totalCandidateCount} distinct candidate address${totalCandidateCount !== 1 ? 'es' : ''} so far. ${rightItems.length} ${rightItems.length === 1 ? 'is currently selected' : 'are currently selected'} on the right, and ${leftItems.length} ${leftItems.length === 1 ? 'stays' : 'stay'} on the left for review.`
    : `Found ${totalCandidateCount} distinct candidate address${totalCandidateCount !== 1 ? 'es' : ''} on ${referencePostcode || 'the selected building postcode'}. ${rightItems.length} ${rightItems.length === 1 ? 'is currently selected' : 'are currently selected'} on the right and ${leftItems.length} ${leftItems.length === 1 ? 'stays' : 'stay'} on the left for review.`
  const leftEmptyMessage = nearbyExpanded
    ? nearbySearchResult === 'found'
      ? nearbyReviewCount > 0
        ? `Nearby postcode search found ${nearbyReviewCount} address${nearbyReviewCount !== 1 ? 'es' : ''}, but they have all been moved out of the left column.`
        : `Nearby postcode search completed, but no extra addresses needed to stay in the left column${nearbyRadiusText ? ` after checking ${nearbyRadiusText}` : ''}.`
      : `No extra nearby postcode addresses were found${nearbyRadiusText ? ` within ${nearbyRadiusText}` : ''}.`
    : 'No other checked addresses were found for this preview'
  const filteredLeftEmptyMessage = normalizedSearchQuery
    ? `No addresses in this column match "${searchQuery.trim()}".`
    : leftEmptyMessage
  const filteredRightEmptyMessage = normalizedSearchQuery
    ? `No selected units in this column match "${searchQuery.trim()}".`
    : 'No units selected'

  const nearbySummaryText = nearbyExpanded
    ? nearbySearchResult === 'found'
      ? `Nearby postcode search completed${nearbyFoundRadius ? ` at ${nearbyFoundRadius}m` : ''}.`
      : `Nearby postcode search completed${nearbyRadiusText ? ` after checking ${nearbyRadiusText}` : ''}.`
    : null

  const nearbyOutcomeText = nearbyExpanded
    ? nearbySearchResult === 'found'
      ? nearbyReviewCount > 0
        ? matchedFromNearbyPostcodesCount > 0
          ? `${nearbyReviewCount} separate nearby address${nearbyReviewCount !== 1 ? 'es were' : ' was'} kept on the left for review, including ${matchedFromNearbyPostcodesCount} that still look like this same building. They are extra candidates, not duplicates of the right column.`
          : `${nearbyReviewCount} nearby address${nearbyReviewCount !== 1 ? 'es were' : ' was'} added to the left column for review.`
        : 'Nearby postcode search completed, but it did not add any new addresses to review.'
      : nearbyMaxRadiusReached
        ? `No nearby postcode results were found after checking every configured radius up to ${nearbyCurrentRadius || nearbyAttemptedRadii[nearbyAttemptedRadii.length - 1] || 100}m.`
        : `No nearby postcode results were found yet.`
    : null

  const rightSummaryText = nearbyExpanded
    ? nearbySearchResult === 'found'
      ? nearbyReviewCount > 0
        ? `${rightItems.length} address${rightItems.length !== 1 ? 'es are' : ' is'} currently selected on the right for creation.`
        : `The right-hand list currently contains ${rightItems.length} selected address${rightItems.length !== 1 ? 'es' : ''} for creation.`
      : `Only ${referencePostcode || 'the selected postcode'} produced matching units, so the right-hand list stayed based on that postcode.`
    : null
  const nearbyStatusText = nearbyExpanded
    ? nearbySearchResult === 'found'
      ? `Checked radius${nearbyAttemptedRadii.length > 1 ? 'es' : ''}: ${nearbyRadiusProgressText || `${nearbyFoundRadius}m`}${nearbyMaxRadiusReached ? ' (max reached)' : ''}.`
      : `Checked radius${nearbyAttemptedRadii.length > 1 ? 'es' : ''}: ${nearbyRadiusProgressText || nearbyRadiusText || 'selected range'}${nearbyMaxRadiusReached ? ' (max reached)' : ''}.`
    : null
  const resultExplanationText =
    previewResultSummary?.message ||
    (nearbyExpanded &&
    matchedFromNearbyPostcodesCount > 0 &&
    unmatchedFromNearbyPostcodesCount === 0
      ? 'We found more addresses while checking nearby postcodes. They are separate addresses and UPRNs, not duplicates of the right column. They still look like the same building, but they stay on the left because they use a different postcode and need review.'
      : null)
  const getUnitIcon = item => {
    const label =
      `${item.unit_name || ''} ${item.address || ''} ${item.building_name || ''}`.toLowerCase()
    const looksLikeApartment =
      item.is_sub_building ||
      label.includes('apartment') ||
      label.includes('flat') ||
      label.includes('unit ') ||
      label.startsWith('unit') ||
      label.includes('suite')

    if (looksLikeApartment) {
      return <ApartmentOutlinedIcon fontSize='small' />
    }

    if (item.building_type === 'residential') {
      return <HomeOutlinedIcon fontSize='small' />
    }

    switch (item.building_type) {
      case 'office':
      case 'hotel':
        return <BusinessOutlinedIcon fontSize='small' />
      case 'retail':
        return <StorefrontOutlinedIcon fontSize='small' />
      case 'industrial':
        return <FactoryOutlinedIcon fontSize='small' />
      case 'assembly':
        return <FestivalOutlinedIcon fontSize='small' />
      case 'recreation':
      case 'leisure':
        return <SportsEsportsOutlinedIcon fontSize='small' />
      case 'storage':
        return <Inventory2OutlinedIcon fontSize='small' />
      default:
        return <HomeOutlinedIcon fontSize='small' />
    }
  }

  const getGroupName = group => {
    if (group.groupingMode === 'review' && group.postcode) {
      return `Postcode ${group.postcode}`
    }

    if (group.buildingName) {
      return group.buildingName
    }

    if (group.thoroughfareName) {
      return group.thoroughfareName
    }

    const firstItem = group.items[0]
    const address = firstItem?.address || ''
    const unitName = firstItem?.unit_name || ''

    if (unitName) {
      return unitName
    }

    if (address) {
      return address
    }

    return 'Address group'
  }

  const getGroupSubtext = group => {
    const firstItem = group.items[0]

    if (group.groupingMode === 'review' && group.postcode) {
      return `${group.items.length} separate candidate address${group.items.length !== 1 ? 'es' : ''}${group.thoroughfareName ? ` around ${group.thoroughfareName}` : ''}`
    }

    if (group.buildingName && group.thoroughfareName) {
      return `${group.thoroughfareName} · ${group.items.length} address${group.items.length !== 1 ? 'es' : ''}`
    }

    if (group.thoroughfareName) {
      return `${group.items.length} address${group.items.length !== 1 ? 'es' : ''} on ${group.thoroughfareName}`
    }

    if (firstItem?.address) {
      return firstItem.address
    }

    return `${group.items.length} address${group.items.length !== 1 ? 'es' : ''}`
  }

  const renderAddressCard = (item, side) => (
    <AddressCard
      key={item.uprn}
      selected={selectedUprns.has(item.uprn)}
      onClick={() => toggleSelect(item.uprn)}
    >
      <CardCheckbox>
        <Checkbox
          name={`${side}-${item.uprn}`}
          isChecked={selectedUprns.has(item.uprn)}
          onChange={() => toggleSelect(item.uprn)}
        />
      </CardCheckbox>
      <CardContent>
        <CardTitle>{item.unit_name}</CardTitle>
        <CardAddress>{item.address}</CardAddress>
        <CardMeta>
          {item.building_type_label && (
            <CardType>{item.building_type_label}</CardType>
          )}
          <CardUprn>UPRN: {item.uprn}</CardUprn>
        </CardMeta>
      </CardContent>
      <CardIcon>{getUnitIcon(item)}</CardIcon>
    </AddressCard>
  )

  const renderGroups = (groups, side, emptyMessage) => {
    if (groups.length === 0) {
      return <EmptyMessage>{emptyMessage}</EmptyMessage>
    }

    return groups.map(group =>
      group.items.length === 1 ? (
        renderAddressCard(group.items[0], side)
      ) : (
        <GroupWrapper key={`${side}-${group.key}`}>
          <GroupHeader onClick={() => toggleGroup(`${side}-${group.key}`)}>
            <GroupLeft>
              {!normalizedSearchQuery &&
              collapsedGroups.has(`${side}-${group.key}`) ? (
                <ExpandMoreIcon fontSize='small' />
              ) : (
                <ExpandLessIcon fontSize='small' />
              )}
              <GroupInfo>
                <GroupName>{getGroupName(group)}</GroupName>
                <GroupToid>{getGroupSubtext(group)}</GroupToid>
              </GroupInfo>
            </GroupLeft>
            <GroupCount>
              {group.items.length} unit{group.items.length !== 1 ? 's' : ''}
            </GroupCount>
          </GroupHeader>
          {(normalizedSearchQuery ||
            !collapsedGroups.has(`${side}-${group.key}`)) && (
            <GroupItems>
              {group.items.map(item => renderAddressCard(item, side))}
            </GroupItems>
          )}
        </GroupWrapper>
      )
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onSkip}
      maxWidth={false}
      sx={{
        zIndex: 99999,
        '& .MuiDialog-paper': {
          backgroundColor: '#f7f8fa',
          borderRadius: '16px',
          width: 'calc(100vw - 80px)',
          height: 'calc(100vh - 80px)',
          maxWidth: '1400px',
          maxHeight: 'calc(100vh - 80px)',
          margin: '40px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <DialogHeader>
        <HeaderLeft>
          <DialogTitle>Select units for this building</DialogTitle>
          <DialogSubtitle>
            Review the addresses found for this building.
            {referenceAddress
              ? ` Matching is based on ${referenceAddress}. `
              : ' '}
            The right column shows the addresses currently selected for
            creation. The left column shows separate candidate addresses that
            still need review. Move items between columns as needed.
          </DialogSubtitle>
        </HeaderLeft>
        <CloseButton type='button' onClick={onSkip}>
          <CloseIcon />
        </CloseButton>
      </DialogHeader>

      <DialogBody>
        {loading ? (
          <LoadingWrapper>
            <CircularProgress size={40} />
            <LoadingText>Loading addresses for this building...</LoadingText>
            <LoadingSubtext>
              This may take a moment for larger buildings
            </LoadingSubtext>
          </LoadingWrapper>
        ) : !previewData ? (
          <LoadingWrapper>
            <LoadingText>No data available</LoadingText>
          </LoadingWrapper>
        ) : !hasRawPreviewResults ? (
          <LoadingWrapper>
            <EmptyTitle>No addresses found</EmptyTitle>
            <LoadingText>
              We could not find any addresses for this building yet.
            </LoadingText>
            <SkipButton type='button' onClick={onSkip}>
              Continue without importing
            </SkipButton>
          </LoadingWrapper>
        ) : !hasActionablePreviewResults && existingItems.length > 0 ? (
          <LoadingWrapper>
            <EmptyTitle>All matching units are already attached</EmptyTitle>
            <LoadingText>
              We found addresses for this building, but they already exist in
              your unit list.
            </LoadingText>
            <LoadingSubtext>
              You can continue without importing duplicates.
            </LoadingSubtext>
            <SkipButton type='button' onClick={onSkip}>
              Continue
            </SkipButton>
          </LoadingWrapper>
        ) : (
          <>
            <SearchModeNotice>
              <SearchModeCopy>
                <SearchModeText>
                  {nearbyExpanded
                    ? nearbySearchResult === 'found'
                      ? summaryHeadline
                      : `Showing addresses from ${referencePostcode || 'the selected building postcode'} only. We also checked nearby postcodes${nearbyRadiusText ? ` within ${nearbyRadiusText}` : ''}, but none were found.`
                    : summaryHeadline}
                </SearchModeText>
                {nearbyOutcomeText && (
                  <SearchModeMeta strong>{nearbyOutcomeText}</SearchModeMeta>
                )}
                {osSelectedUprnSet.size > 0 && (
                  <SearchModeMeta strong>
                    {osSelectedUprnSet.size} UPRN
                    {osSelectedUprnSet.size === 1 ? '' : 's'} from the OS map
                    selection {osSelectedUprnSet.size === 1 ? 'is' : 'are'}{' '}
                    preselected for creation. Move any address back to the left
                    if it should not become a unit.
                    {missingOsSelectedUprns.length > 0
                      ? ` ${missingOsSelectedUprns.length} selected UPRN${missingOsSelectedUprns.length === 1 ? '' : 's'} could not be resolved in NGD yet.`
                      : ''}
                  </SearchModeMeta>
                )}
                {truncationWarningText && (
                  <SearchModeMeta strong>
                    {truncationWarningText}
                  </SearchModeMeta>
                )}
                {nearbyExpanded && (
                  <SearchModeMeta>
                    {nearbyStatusText}
                    {nearbyCurrentRadius && !nearbyStatusText
                      ? ` Current radius: ${nearbyCurrentRadius}m.`
                      : ''}
                  </SearchModeMeta>
                )}
                {(referencePostcodeRawCount > 0 || nearbyExpanded) && (
                  <SearchModeMeta>
                    {referencePostcode
                      ? `${referencePostcode}: ${referencePostcodeRawCount} raw, ${referencePostcodeDedupedCount} after dedupe, ${referencePostcodeMatchedCount} auto-selected.`
                      : null}
                    {nearbyExpanded && nearbyPagesStatusText
                      ? ` ${nearbyPagesStatusText}`
                      : ''}
                    {nearbyExpanded && nearbyDiscoveredPostcodesCount > 0
                      ? ` Nearby discovery: ${nearbyDiscoveredPostcodesCount} postcode${nearbyDiscoveredPostcodesCount !== 1 ? 's' : ''}, ${nearbyDedupedCount} deduped address${nearbyDedupedCount !== 1 ? 'es' : ''}.`
                      : ''}
                    {nearbyExpanded && nearbyPostcodeLimitReached
                      ? ` More nearby postcodes were discovered (${nearbyDiscoveredPostcodesTotalCount}), but only the top ${nearbyDiscoveredPostcodesCount} were checked because of the postcode limit.`
                      : ''}
                    {nearbyExpanded && nearbyReversegeoPageLimitReached
                      ? ' The reverse-geo page limit was reached for this radius.'
                      : ''}
                    {nearbyExpanded && nearbyPostcodesPageLimitReached
                      ? ' A page limit was reached for at least one nearby postcode.'
                      : ''}
                  </SearchModeMeta>
                )}
              </SearchModeCopy>
            </SearchModeNotice>
            {nearbySummaryText && (
              <NearbyResultBanner>
                <NearbyResultTitle>{nearbySummaryText}</NearbyResultTitle>
                <NearbyResultBody>
                  {totalCandidateCount} candidate address
                  {totalCandidateCount !== 1 ? 'es' : ''} found in total.{' '}
                  {rightSummaryText}
                  {resultExplanationText ? ` ${resultExplanationText}` : ''}
                  {nearbyReviewCount > 0 && (
                    <>
                      {' '}
                      {nearbyReviewCount} nearby address
                      {nearbyReviewCount !== 1 ? 'es are' : ' is'} on the left
                      for review.
                    </>
                  )}
                </NearbyResultBody>
              </NearbyResultBanner>
            )}
            <SearchToolbar>
              <SearchFieldWrapper>
                <SearchInput
                  type='text'
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder='Search by UPRN, postcode, address, unit name or building'
                />
              </SearchFieldWrapper>
              <SearchToolbarMeta>
                {normalizedSearchQuery
                  ? `Showing ${totalVisibleResults} match${totalVisibleResults !== 1 ? 'es' : ''} across both columns`
                  : 'Filter results by UPRN, postcode, address, unit name or building'}
                {normalizedSearchQuery && (
                  <SearchClearButton
                    type='button'
                    onClick={() => setSearchQuery('')}
                  >
                    Clear search
                  </SearchClearButton>
                )}
              </SearchToolbarMeta>
            </SearchToolbar>
            <TransferContainer>
              <PanelColumn>
                <PanelHeader>
                  <PanelHeaderContent>
                    <PanelTitle>Additional candidates to review</PanelTitle>
                    <PanelSubtitle>
                      Separate addresses and UPRNs that were checked but not
                      auto-selected yet
                      {normalizedSearchQuery
                        ? ` Showing ${filteredLeftItems.length} of ${leftItems.length}.`
                        : ''}
                    </PanelSubtitle>
                  </PanelHeaderContent>
                  <PanelHeaderSide>
                    <PanelActionButton
                      type='button'
                      onClick={() =>
                        updateSelectionForItems(
                          filteredLeftItems,
                          !allVisibleLeftSelected
                        )
                      }
                      disabled={filteredLeftItems.length === 0}
                    >
                      {allVisibleLeftSelected
                        ? 'Clear visible'
                        : 'Select all visible'}
                    </PanelActionButton>
                    <PanelCount>
                      {normalizedSearchQuery
                        ? `${filteredLeftItems.length}/${leftItems.length}`
                        : leftItems.length}
                    </PanelCount>
                  </PanelHeaderSide>
                </PanelHeader>
                <PanelList>
                  {renderGroups(leftGroups, 'left', filteredLeftEmptyMessage)}
                </PanelList>
              </PanelColumn>

              <ArrowsColumn>
                <ArrowButton
                  type='button'
                  onClick={moveSelectedToRight}
                  disabled={
                    ![...selectedUprns].some(u =>
                      filteredLeftItems.find(i => i.uprn === u)
                    )
                  }
                  title='Move selected to right'
                >
                  <ArrowForwardIcon />
                </ArrowButton>
                <ArrowButton
                  type='button'
                  onClick={moveSelectedToLeft}
                  disabled={
                    ![...selectedUprns].some(u =>
                      filteredRightItems.find(i => i.uprn === u)
                    )
                  }
                  title='Move selected to left'
                >
                  <ArrowBackIcon />
                </ArrowButton>
              </ArrowsColumn>

              <PanelColumn highlight>
                <PanelHeader>
                  <PanelHeaderContent>
                    <PanelTitle>Units to create</PanelTitle>
                    <PanelSubtitle>
                      Addresses confidently matching your building
                      {normalizedSearchQuery
                        ? ` Showing ${filteredRightItems.length} of ${rightItems.length}.`
                        : ''}
                    </PanelSubtitle>
                  </PanelHeaderContent>
                  <PanelHeaderSide>
                    <PanelActionButton
                      type='button'
                      onClick={() =>
                        updateSelectionForItems(
                          filteredRightItems,
                          !allVisibleRightSelected
                        )
                      }
                      disabled={filteredRightItems.length === 0}
                    >
                      {allVisibleRightSelected
                        ? 'Clear visible'
                        : 'Select all visible'}
                    </PanelActionButton>
                    <PanelCount highlight>
                      {normalizedSearchQuery
                        ? `${filteredRightItems.length}/${rightItems.length}`
                        : rightItems.length}
                    </PanelCount>
                  </PanelHeaderSide>
                </PanelHeader>
                <PanelList>
                  {renderGroups(
                    rightGroups,
                    'right',
                    filteredRightEmptyMessage
                  )}
                </PanelList>
              </PanelColumn>
            </TransferContainer>
          </>
        )}
      </DialogBody>

      {!loading && previewData && hasActionablePreviewResults && (
        <DialogFooter>
          <FooterInfo>
            {totalToCreate} unit{totalToCreate !== 1 ? 's' : ''} will be created
          </FooterInfo>
          <FooterActions>
            <SkipButton type='button' onClick={onSkip}>
              Skip
            </SkipButton>
            <ConfirmButton
              type='button'
              onClick={handleConfirm}
              disabled={totalToCreate === 0 || confirming}
            >
              {confirming ? (
                <>
                  <CircularProgress size={16} style={{ color: '#fff' }} />{' '}
                  Creating units...
                </>
              ) : (
                `Create ${totalToCreate} unit${totalToCreate !== 1 ? 's' : ''}`
              )}
            </ConfirmButton>
          </FooterActions>
        </DialogFooter>
      )}
    </Dialog>
  )
}

export default UnitsSyncModal

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 32px 16px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`

const HeaderLeft = styled.div`
  flex: 1;
`

const DialogTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px;
`

const DialogSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  max-width: 600px;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`

const DialogBody = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 20px 32px;
  display: flex;
  flex-direction: column;
`

const DialogFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 32px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
`

const FooterInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

const LoadingSubtext = styled.div`
  font-size: 13px;
  color: #9ca3af;
`

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
`

const TransferContainer = styled.div`
  display: flex;
  gap: 12px;
  flex: 1;
  min-height: 0;
`

const SearchToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

const SearchFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid #dbe7f0;
  background: #fff;
  color: #1f2937;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #1eb8ce;
    box-shadow: 0 0 0 3px rgba(30, 184, 206, 0.12);
  }
`

const SearchToolbarMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
`

const SearchClearButton = styled.button`
  border: none;
  background: none;
  color: #1295a8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #0f766e;
  }
`

const NearbyResultBanner = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid #d9edf2;
  border-radius: 10px;
  background: #f5fdff;
`

const NearbyResultTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1295a8;
  margin-bottom: 4px;
`

const NearbyResultBody = styled.div`
  font-size: 13px;
  color: #4b5563;
  line-height: 1.5;
`

const SearchModeNotice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid #dbe7f0;
  border-radius: 10px;
  background: #fff;
`

const SearchModeCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`

const SearchModeText = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`

const SearchModeMeta = styled.div`
  font-size: 12px;
  color: ${({ strong }) => (strong ? '#0f766e' : '#1295a8')};
  font-weight: ${({ strong }) => (strong ? 700 : 600)};
  line-height: 1.4;
`

const PanelColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 2px solid ${({ highlight }) => (highlight ? '#1eb8ce' : '#e5e7eb')};
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
`

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafbfc;
  flex-shrink: 0;
`

const PanelHeaderContent = styled.div`
  flex: 1;
`

const PanelHeaderSide = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 16px;
`

const PanelTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
`

const PanelSubtitle = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
`

const PanelCount = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background: ${({ highlight }) => (highlight ? '#1eb8ce' : '#9ca3af')};
  border-radius: 16px;
  padding: 4px 14px;
  min-width: 28px;
  text-align: center;
`

const PanelActionButton = styled.button`
  border: none;
  background: none;
  color: #1295a8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: #0f766e;
  }

  &:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
  }
`

const PanelList = styled.div`
  flex: 1;
  overflow-y: auto;
`

const AddressCard = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 20px;
  cursor: pointer;
  background: ${({ selected }) => (selected ? '#ecfeff' : '#fff')};
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;

  &:hover {
    background: ${({ selected }) => (selected ? '#ecfeff' : '#f9fafb')};
  }
`

const CardCheckbox = styled.div`
  flex-shrink: 0;
  width: 24px;

  label {
    min-width: 20px;
    width: 20px;
    padding-left: 22px;
  }
`

const CardContent = styled.div`
  margin-left: 8px;
  min-width: 0;
  flex: 1;
`

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.3;
  min-width: 0;
`

const CardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: center;
  color: #6b7280;
  flex-shrink: 0;
  justify-content: center;
  margin-left: 12px;
`

const CardAddress = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.3;
`

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
`

const CardType = styled.span`
  font-size: 12px;
  color: #1eb8ce;
  font-weight: 600;
  background: #ecfeff;
  padding: 2px 8px;
  border-radius: 4px;
`

const CardUprn = styled.span`
  font-size: 12px;
  color: #9ca3af;
`

const GroupWrapper = styled.div`
  &:not(:last-child) {
    border-bottom: 2px solid #e5e7eb;
  }
`

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #f3f4f6;
  cursor: pointer;
  position: sticky;
  top: 0;
  z-index: 1;

  &:hover {
    background: #e5e7eb;
  }
`

const GroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const GroupInfo = styled.div``

const GroupName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`

const GroupToid = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 1px;
`

const GroupCount = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  background: #e5e7eb;
  border-radius: 12px;
  padding: 3px 10px;
`

const GroupItems = styled.div``

const ArrowsColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
`

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  color: #1eb8ce;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #1eb8ce;
    color: #fff;
    border-color: #1eb8ce;
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
`

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  font-size: 14px;
  color: #9ca3af;
`

const SkipButton = styled.button`
  background: #fff;
  border: 1px solid #d1d5db;
  color: #6b7280;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`

const ConfirmButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #1eb8ce;
  border: none;
  color: #fff;
  padding: 10px 28px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #17a0b4;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
